import { iHotelSearchResponse } from "../../../interface";
import { iHotelInputPayload } from "../../../types";

export interface MapAreaCentre {
  latitude: string;
  longitude: string;
}

export interface MapAreaPlace {
  city: string;
  country: string;
}

const AREA_DECIMALS = 4;
const NEAREST_STAYS = 5;
const RADIUS_IMPORTANCE = 5;

export const cityName = (city?: string | null) => (city ? city.split(",")[0] : "");

export const toMapAreaCentre = (lat: number, lng: number): MapAreaCentre => ({
  latitude: lat.toFixed(AREA_DECIMALS),
  longitude: lng.toFixed(AREA_DECIMALS),
});

export const isMapAreaEntry = (entry?: Partial<iHotelInputPayload> | null): boolean =>
  !!entry?.mapAreaRadiusMi &&
  entry.mapAreaCentre?.latitude === entry.latitude &&
  entry.mapAreaCentre?.longitude === entry.longitude;

export const isMapAreaStay = (stay?: Partial<iHotelInputPayload> | null): boolean =>
  !!stay?.city && isMapAreaEntry(stay) && !!stay.mapAreaLabel && stay.selectedLocation === stay.mapAreaLabel;

export const hasSearchPlace = (entry?: Partial<iHotelInputPayload> | null): boolean =>
  !!entry?.city?.trim() || isMapAreaEntry(entry);

export const mapAreaFields = (
  stay?: Partial<iHotelInputPayload> | null
): Pick<iHotelInputPayload, "mapAreaRadiusMi" | "mapAreaCentre"> =>
  isMapAreaEntry(stay) ? { mapAreaRadiusMi: stay?.mapAreaRadiusMi, mapAreaCentre: stay?.mapAreaCentre } : {};

export const stayCityLabel = (stay?: Partial<iHotelInputPayload> | null): string | null => {
  if (!stay?.city) return null;
  return isMapAreaStay(stay) ? stay.mapAreaLabel ?? null : cityName(stay.city);
};

export const toMapAreaEntry = (
  stay: Pick<iHotelInputPayload, "checkIn" | "checkOut" | "country">,
  centre: MapAreaCentre,
  radiusMi: number
): iHotelInputPayload => ({
  checkIn: stay.checkIn,
  checkOut: stay.checkOut,
  country: stay.country,
  city: "",
  latitude: centre.latitude,
  longitude: centre.longitude,
  mapAreaRadiusMi: radiusMi,
  mapAreaCentre: centre,
});

export const toMapAreaStay = <T extends iHotelInputPayload>(
  stay: T,
  centre: MapAreaCentre,
  radiusMi: number,
  place: MapAreaPlace
): T => ({
  ...stay,
  latitude: centre.latitude,
  longitude: centre.longitude,
  city: place.city,
  country: place.country,
  hotelName: "",
  destinationAddress: "",
  streetNumber: "",
  route: "",
  placeId: "",
  locality: place.city,
  selectedLocation: place.city,
  mapAreaLabel: place.city,
  mapAreaRadiusMi: radiusMi,
  mapAreaCentre: centre,
  accommodation_location_name: place.city,
});

export function withMapAreaRadius<T extends iHotelInputPayload>(entry: T) {
  const { mapAreaRadiusMi, mapAreaCentre, ...city } = entry;
  if (!mapAreaRadiusMi || !isMapAreaEntry(entry)) return city;
  return {
    ...city,
    city: "",
    preferences: {
      location: [{ name: "radius", value: mapAreaRadiusMi, unit: "miles", importance: RADIUS_IMPORTANCE }],
    },
  };
}

export const cityRowsOf = (response?: iHotelSearchResponse | null): any[] => {
  const data: any = response?.data;
  const rows = Array.isArray(data) ? data : data?.data;
  return Array.isArray(rows) ? rows : [];
};

export function withRequestCities(
  response: iHotelSearchResponse,
  cities: Pick<iHotelInputPayload, "city">[]
): iHotelSearchResponse {
  const rows = cityRowsOf(response);
  const cityAt = (index: number) => (cities[index] ?? cities[0])?.city;
  const unnamed = (row: any, index: number) => !!row && !row.city && !!cityAt(index);
  if (!rows.some(unnamed)) return response;
  const named = rows.map((row, index) => (unnamed(row, index) ? { ...row, city: cityAt(index) } : row));
  const data: any = response.data;
  return { ...response, data: Array.isArray(data) ? named : { ...data, data: named } };
}

const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");

const distanceOf = (hotel: any): number => {
  const raw = hotel?.distance ?? hotel?.hotelDetails?.distance;
  return typeof raw === "number" ? raw : parseFloat(raw);
};

const mostCommon = (values: string[]): string | undefined => {
  const counts = new Map<string, number>();
  values.forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1));
  return values.reduce<string | undefined>(
    (best, value) => (best === undefined || (counts.get(value) ?? 0) > (counts.get(best) ?? 0) ? value : best),
    undefined
  );
};

export function nearestStaysPlace(response: iHotelSearchResponse, fallback: MapAreaPlace): MapAreaPlace {
  const nearest = cityRowsOf(response)
    .flatMap((row) => (Array.isArray(row?.hotels) ? row.hotels : []))
    .map((hotel) => ({ hotel, distance: distanceOf(hotel) }))
    .filter(({ distance }) => Number.isFinite(distance))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, NEAREST_STAYS)
    .map(({ hotel }) => hotel);
  const town = mostCommon(nearest.map((hotel) => text(hotel?.hotelDetails?.city) || text(hotel?.city)).filter(Boolean));
  const country = mostCommon(
    nearest.map((hotel) => (text(hotel?.hotelDetails?.country) || text(hotel?.country)).toUpperCase()).filter(Boolean)
  );
  return { city: town ?? fallback.city, country: country ?? fallback.country };
}
